import cn from 'clsx'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { readCSSVar, themeClassName } from 'lib/theme/themeUtils'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Root = styled.div`
  box-shadow: none;
  transition: box-shadow 0.2s;
  color: ${props => props.theme.palette.neutral.default.one};
  padding: var(--pane-padding);
  transition: background-color 200ms, border-color 200ms, box-shadow 200ms;

  ${mediaQueryUp.tablet} {
    padding: ${readCSSVar('--pane-tablet-padding', '--pane-padding')};
  }

  ${mediaQueryUp.desktop} {
    padding: ${readCSSVar('--pane-desktop-padding', '--pane-tablet-padding', '--pane-padding')};
  }

  &.bordered { border: 1px solid transparent; }
  &.border-transparent { border-color: transparent; }
  &.border-primary { border-color: ${props => props.theme.palette.brand.primary.normal}; }
  &.border-critical { border-color: ${props => props.theme.palette.messaging.critical.normalForeground}; }
  &.border-highlight-secondary { border-color: ${props => props.theme.palette.highlight.secondary.normalForeground}; }
  &.border-neutral-one { border-color: ${props => props.theme.palette.neutral.default.one}; }
  &.border-neutral-five { border-color: ${props => props.theme.palette.neutral.default.five}; }
  &.border-neutral-six { border-color: ${props => props.theme.palette.neutral.default.six}; }
  &.border-tier-bronze { border-color: ${props => props.theme.palette.product.loyalty.bronze.normalForeground}; }
  &.border-tier-silver { border-color: ${props => props.theme.palette.product.loyalty.silver.normalForeground}; }
  &.border-tier-gold { border-color: ${props => props.theme.palette.product.loyalty.gold.normalForeground}; }
  &.border-tier-platinum { border-color: ${props => props.theme.palette.product.loyalty.platinum.normalForeground}; }

  &.background-neutral-six {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }

  &.background-neutral-seven {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.background-neutral-eight {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  &.background-lux-plus {
    background-color: ${props => props.theme.palette.product.luxPlus.background};
    color: ${props => props.theme.palette.product.luxPlus.contrast};
  }

  &.background-tier-bronze-primary {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
    background-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast}
  }
  &.background-tier-bronze-dark {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast}
  }
  &.background-tier-bronze-light {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.lightContrast}
  }

  &.background-tier-silver-primary {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientSecondaryBackground};
    background-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast}
  }
  &.background-tier-silver-dark {
    background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.darkContrast}
  }
  &.background-tier-silver-light {
    background-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.lightContrast}
  }

  &.background-tier-gold-primary {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientSecondaryBackground};
    background-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast}
  }
  &.background-tier-gold-dark {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.darkContrast}
  }
  &.background-tier-gold-light {
    background-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.lightContrast}
  }

  &.background-tier-platinum-primary {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientSecondaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
  &.background-tier-platinum-dark {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast}
  }
  &.background-tier-platinum-light {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.lightContrast}
  }

  &.shadow-bottom-small { box-shadow: ${props => props.theme.shadow.bottom.small}; }
  &.shadow-bottom-medium { box-shadow: ${props => props.theme.shadow.bottom.medium}; }
  &.shadow-bottom-large { box-shadow: ${props => props.theme.shadow.bottom.large}; }
  &.shadow-flat-small { box-shadow: ${props => props.theme.shadow.flat.small}; }
  &.shadow-flat-large { box-shadow: ${props => props.theme.shadow.flat.large}; }
  &.shadow-top-small { box-shadow: ${props => props.theme.shadow.top.small}; }
  &.shadow-top-medium { box-shadow: ${props => props.theme.shadow.top.medium}; }
  &.shadow-top-large { box-shadow: ${props => props.theme.shadow.top.large}; }

  &.border-radius-XS { border-radius: ${props => props.theme.borderRadius.XS}; }
  &.border-radius-S { border-radius: ${props => props.theme.borderRadius.S}; }
  &.border-radius-M { border-radius: ${props => props.theme.borderRadius.M}; }
  &.border-radius-L { border-radius: ${props => props.theme.borderRadius.L}; }
  &.border-radius-round { border-radius: ${props => props.theme.borderRadius.round}; }
`

type PanePadding = number
| `${number} ${number}`
| `${number} ${number} ${number}`
| `${number} ${number} ${number} ${number}`

interface Props extends React.ComponentProps<typeof Root> {
  /**
   * Background colour of the pane
   * @default neutral-eight
   */
  background?: 'none' | 'neutral-seven' | 'neutral-eight' | 'neutral-six' | 'lux-plus' | `tier-${App.LoyaltyTier}-primary` | `tier-${App.LoyaltyTier}-dark` | `tier-${App.LoyaltyTier}-light`;
  /**
   * Adds a box-shadow to the pane, use this to make it look 'raised'
   */
  shadow?: 'bottom-small' | 'bottom-medium' | 'bottom-large' | 'flat-small' | 'flat-large' | 'top-small' | 'top-medium' | 'top-large';
  border?: 'transparent' | 'primary' | 'critical' | 'highlight-secondary' | 'neutral-one' | 'neutral-five' | 'neutral-six' | `tier-${App.LoyaltyTier}`;
  borderRadius?: App.Design.BorderRadius
  /**
   * Sets the palette for the pane and it's contents at mobile and up sizes
   */
  paletteType?: App.Design.PaletteType
    /**
   * Sets the palette for the pane and it's contents at tablet and up sizes
   */
  tabletPaletteType?: App.Design.PaletteType
    /**
   * Sets the palette for the pane and it's contents at desktop and up sizes
   */
  desktopPaletteType?: App.Design.PaletteType
  /**
   * Apply padding to the pane. Same format as the CSS 'padding' value
   * e.g. 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @example 20 20
   */
  padding?: PanePadding;
  /**
   * Apply padding to the pane at tablet sizes and up. Same format as the CSS 'padding' value
   * e.g. 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @example 20 20
   */
  tabletPadding?: PanePadding;
  /**
   * Apply padding to the pane at desktop sizes and up. Same format as the CSS 'padding' value
   * e.g. 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @example 20 20
   */
  desktopPadding?: PanePadding;
}

function getPadding(padding: PanePadding | undefined): string | undefined {
  if (!padding) {
    return '0'
  }

  if (typeof padding === 'number') {
    return rem(padding)
  }

  if (typeof padding === 'string') {
    // we need to convert numbers to rem
    const parts = padding.split(' ')
    return parts.map(p => rem(+p)).join(' ')
  }
}

function Pane({
  children,
  shadow,
  className,
  border,
  borderRadius,
  paletteType,
  tabletPaletteType,
  desktopPaletteType,
  padding,
  tabletPadding,
  desktopPadding,
  background = 'neutral-eight',
  ref,
  ...rest
}: Props) {
  const paddingCss = useMemo(() => getPadding(padding), [padding])
  const tabletPaddingCss = useMemo(() => getPadding(tabletPadding ?? padding), [tabletPadding, padding])
  const desktopPaddingCss = useMemo(() => getPadding(desktopPadding ?? tabletPadding ?? padding), [desktopPadding, tabletPadding, padding])

  return (
    <Root
      {...rest}
      className={cn(
        className,
        themeClassName({
          base: paletteType,
          tabletAndUp: tabletPaletteType,
          // types are complicated here, but it's fine, I promise
          desktopAndUp: desktopPaletteType as any,
        }),
        `background-${background}`,
        border ? `border-${border}` : undefined,
        shadow ? `shadow-${shadow}` : undefined,
        borderRadius ? `border-radius-${borderRadius}` : undefined,
        { bordered: !!border },
      )}
      style={{
        ...rest.style,
        '--pane-padding': paddingCss,
        '--pane-tablet-padding': tabletPaddingCss,
        '--pane-desktop-padding': desktopPaddingCss,
      }}
      ref={ref}
    >
      {children}
    </Root>
  )
}

export default React.memo(Pane)
